import { useTranslation } from "react-i18next";
import { useFormContext, Controller } from "react-hook-form";
import {
  Box,
  FormLabel,
  TextField,
  FormControl,
  Typography,
  MenuItem,
  Select,
  FormHelperText,
  IconButton,
} from "@mui/material";

import { CreateProgramFormData } from "../../../../types/service.type";
import { programLevelList, serviceCategoryList, serviceSubCategoryList } from "../../../../enum/service.enum";
import SelectServiceBannerImageModal from "../../SelectServiceBannerImageModal";
import { useState } from "react";
import { useServiceBanners } from "../../../../api/queries/useServiceBanners";
import {
  DeleteSelectServiceBannerButtonIcon,
  SelectServiceBannerImageIcon,
} from "../../../../assets/icons/create-service-icons";

const ProgramInformation = () => {
  const { t } = useTranslation("goService", { keyPrefix: "createServicePage.programInformationStep" });
  const { t: tProgramLevel } = useTranslation("goService", { keyPrefix: "programLevel" });
  const { t: tCategories } = useTranslation("goService", { keyPrefix: "serviceCategory" });
  const { t: tSubCategories } = useTranslation("goService", { keyPrefix: "serviceSubCategory" });

  const {
    control,
    formState: { errors },
  } = useFormContext<CreateProgramFormData>();

  const [openSelectServiceBannerImageModal, setOpenSelectServiceBannerImageModal] = useState(false);
  const handleCloseSelectServiceBannerImageModal = () => setOpenSelectServiceBannerImageModal(false);
  const handleOpenSelectServiceBannerImageModal = () => setOpenSelectServiceBannerImageModal(true);

  const { data: banners, isLoading: isLoadingBanners } = useServiceBanners();

  return (
    <>
      <Box sx={styles.container}>
        {/* <Typography sx={styles.serviceCode}>{t("programCode", { code: "19823-AB" })}</Typography> */}
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <FormControl fullWidth component="fieldset" error={!!errors.name}>
              <FormLabel>{t("programName")}</FormLabel>
              <TextField {...field} fullWidth placeholder={t("enterProgramName")} error={!!errors.name} />
              {errors.name && <FormHelperText error>{t(errors.name.message)}</FormHelperText>}
            </FormControl>
          )}
        />
        <FormControl fullWidth component="fieldset" error={!!errors.banner}>
          <FormLabel sx={styles.formControlLabel}>{t("chooseServiceBannerImage")}</FormLabel>
          <Controller
            name="banner"
            control={control}
            render={({ field }) => (
              <>
                {field.value && banners?.find((b) => b.id === field.value)?.url && (
                  <>
                    <Box
                      component="img"
                      src={banners?.find((b) => b.id === field.value)?.url}
                      sx={styles.serviceBannerImage}
                    />
                    <IconButton onClick={() => field.onChange()} size="small" sx={styles.deleteServiceBannerButton}>
                      <DeleteSelectServiceBannerButtonIcon />
                    </IconButton>
                  </>
                )}
                {!field.value && (
                  <Box
                    sx={styles.serviceBannerContainer(!!errors.banner)}
                    onClick={handleOpenSelectServiceBannerImageModal}
                  >
                    <SelectServiceBannerImageIcon />
                    <Typography sx={styles.clickToSelect}>
                      {field.value ? t("changeBanner") : t("clickToSelect")}
                    </Typography>
                  </Box>
                )}
                {errors.banner && <FormHelperText error>{t(errors.banner.message as string)}</FormHelperText>}
              </>
            )}
          />
        </FormControl>
        <Controller
          name="level"
          control={control}
          render={({ field }) => (
            <FormControl fullWidth error={!!errors.level}>
              <FormLabel error={!!errors.level}>{t("programLevel")}</FormLabel>
              <Select {...field} displayEmpty fullWidth error={!!errors.level}>
                <MenuItem selected value="" disabled>
                  {t("selectLevel")}
                </MenuItem>
                {programLevelList.map((level, idx) => (
                  <MenuItem key={idx} value={level}>
                    {tProgramLevel(level)}
                  </MenuItem>
                ))}
              </Select>
              {errors.level && <FormHelperText error>{t(errors.level.message)}</FormHelperText>}
            </FormControl>
          )}
        />
        <Controller
          name="category"
          control={control}
          render={({ field }) => (
            <FormControl fullWidth error={!!errors.category}>
              <FormLabel error={!!errors.category}>{t("programCategory")}</FormLabel>
              <Select {...field} displayEmpty fullWidth error={!!errors.category}>
                <MenuItem selected value="" disabled>
                  {t("selectOneCategory")}
                </MenuItem>
                {serviceCategoryList.map((category, idx) => (
                  <MenuItem key={idx} value={category}>
                    {tCategories(category)}
                  </MenuItem>
                ))}
              </Select>
              {errors.category && <FormHelperText error>{t(errors.category.message)}</FormHelperText>}
            </FormControl>
          )}
        />
        <Controller
          name="subCategory"
          control={control}
          render={({ field }) => (
            <FormControl fullWidth error={!!errors.subCategory}>
              <FormLabel error={!!errors.subCategory}>{t("programSubcategory")}</FormLabel>

              <Select {...field} displayEmpty fullWidth error={!!errors.subCategory}>
                <MenuItem selected value="" disabled>
                  {t("selectOneSubcategory")}
                </MenuItem>
                {serviceSubCategoryList.map((subCategory, idx) => (
                  <MenuItem key={idx} value={subCategory}>
                    {tSubCategories(subCategory)}
                  </MenuItem>
                ))}
              </Select>
              {errors.subCategory && <FormHelperText error>{t(errors.subCategory.message)}</FormHelperText>}
            </FormControl>
          )}
        />
        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <FormControl fullWidth component="fieldset" error={!!errors.description}>
              <FormLabel error={!!errors.description}>{t("describeYourProgram")}</FormLabel>
              <TextField {...field} fullWidth placeholder={t("eGiP")} multiline rows={5} error={!!errors.description} />
              {errors.description && <FormHelperText error>{t(errors.description.message)}</FormHelperText>}
            </FormControl>
          )}
        />
      </Box>
      <SelectServiceBannerImageModal
        open={openSelectServiceBannerImageModal}
        closeModal={handleCloseSelectServiceBannerImageModal}
        banners={banners}
        isLoadingBanners={isLoadingBanners}
      />
    </>
  );
};

export default ProgramInformation;

const styles = {
  container: {
    width: { xs: "100%", md: "512px" },
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  serviceCode: {
    fontFamily: "Montserrat",
    color: "var(--Dark---600, #707070)",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
  },
  formControlLabel: {
    margin: 0,
    gap: "12px",
    fontFamily: "Montserrat",
    color: "var(--Dark---1000, #111)",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "24px",
  },
  serviceBannerImage: {
    width: "100%",
    height: "100px",
    objectFit: "cover",
  },
  deleteServiceBannerButton: {
    position: "absolute",
    top: "30px",
    right: 0,
  },
  serviceBannerContainer: (error: boolean) => ({
    padding: "16px 24px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    borderRadius: "12px",
    background: "var(--Base-White, #FFF)",
    cursor: "pointer",
    border: error
      ? "1px solid var(--Colors-Feedback-Error-error-500, #F24822)"
      : "1px solid var(--Gray---200, #CFCFCF)",
  }),
  clickToSelect: {
    color: "var(--Brand-700, #076D0D)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "20px",
  },
};
